// src/oura/gscState.ts
//
// What the Search Console connection ACTUALLY is, as opposed to what the
// connector catalog claims it is.
//
// The catalog's `status: "connected"` records one fact only: an OAuth grant
// exists. It says nothing about whether a single row of Search Console data
// ever reached a page Oura crawled. Rendering a green "Connected" off that
// field alone told operators a connection was working while every search
// number on the product was empty, and the empty numbers then read as
// "this site ranks for nothing" rather than "we matched nothing".
//
// So the state is derived from BOTH sources — the catalog entry and the scan's
// `results.searchPerformance` — and every surface that shows or implies a GSC
// connection reads it from here. Three surfaces used to decide this
// independently and disagreed with each other (Sprint 0 diagnosis, tracker
// 2026-08-03).
//
// WHAT THIS CAN AND CANNOT TELL YOU
//
// Zero matched rows has three plausible causes, and today's payload separates
// only two of them:
//
//   never synced      provable: the catalog carries `lastSync: null`
//   nothing matched   provable: the scan pulled and no row joined a crawled page
//   wrong property    NOT provable here — nothing on the wire names the
//                     connected property or compares it to the crawled host
//
// The third needs the BE to validate the property at connection time and send
// the result (`propertyUrl` / `propertyMismatch`, see the Sprint 0 handoff).
// Until it does, `no_match` names both remaining causes in its detail line
// rather than guessing one. `propertyMismatch` is honoured only on an explicit
// `=== true`: absent means "not validated", which is not the same as "valid".

import type { AeoSearchPerformance } from "@service/aeo";
import type { DataConnector } from "./connectors";

export type GscStateKind =
    /** No GSC connector in the catalog, or it is not connected. */
    | "not_connected"
    /** OAuth started, never finished. */
    | "action_needed"
    /** Connected, but this scan predates search-performance collection. */
    | "not_measured"
    /** Connected in the catalog, yet the scan reports no Search Console pull. */
    | "no_pull"
    /** Connected, never pulled once. Search Console lags 2 to 3 days. */
    | "never_synced"
    /** Pulled, and not one row matched a page we crawled. */
    | "no_match"
    /** Pulled query-level data (keywords / totals) but no per-page rollup, so
     *  every per-page surface still renders dashes. */
    | "keywords_only"
    /** The BE validated the property against the live host and they differ. */
    | "wrong_property"
    /** Page rows joined to crawled pages. The only state that earns green —
     *  the SAME definition the Pages table uses (`pages.length > 0`), so a
     *  green tick can never sit beside a table of dashes. */
    | "matched";

export interface GscState {
    kind: GscStateKind;
    /**
     * `ok` earns the green "Connected". `warn` means the catalog claims a
     * connection the data does not support — the case this module exists for.
     * `idle` is an honest absence: nothing is connected and nothing is wrong.
     */
    tone: "ok" | "warn" | "idle";
    /** Status line, short enough for a chip. */
    label: string;
    /** One sentence: what it means, and what would change it. */
    detail: string;
    /** Pages Search Console has ever shown, `null` when unknown. */
    matchedPages: number | null;
    /** Pages the crawl reached, `null` when unknown. */
    crawledPages: number | null;
    /** Whether search numbers on other screens can be trusted at all. */
    hasData: boolean;
}

/**
 * Did the pull return anything?
 *
 * Four independent signals, because a payload can carry any subset: a run may
 * have totals but a trimmed `pages` list, or keywords but no page rollup.
 * Requiring all of them would report "no data" on data we are displaying.
 */
export function hasSearchRows(search: AeoSearchPerformance): boolean {
    return (
        (search.pagesWithImpressions ?? 0) > 0
        || (search.pages?.length ?? 0) > 0
        || (search.keywords?.length ?? 0) > 0
        || (search.totals?.impressions ?? 0) > 0
    );
}

export function deriveGscState(input: {
    /** The `gsc` entry from the connector catalog, if the catalog was loaded. */
    connector?: DataConnector | null;
    /** `results.searchPerformance` from the latest scan. */
    search?: AeoSearchPerformance;
    /** `audit.pagesCrawled` — the fallback denominator when the search block
     *  carries none of its own. */
    crawledPages?: number | null;
    /** The host Oura crawls, for the wrong-property sentence. */
    siteDomain?: string;
    /**
     * The crawl's pages carry Search Console clicks (`priorityPages[].visits`,
     * from the per-page pull). A scan can have those and no search block:
     * the Site screen then shows clicks this state must not call unread.
     */
    pageClicks?: boolean;
}): GscState {
    const { connector, search, siteDomain, pageClicks } = input;
    const crawled = search?.pagesCrawled ?? input.crawledPages ?? null;
    const matched = search?.pagesWithImpressions ?? search?.pages?.length ?? null;

    /**
     * "115 of 30 pages matched" is not a sentence anyone can act on, and the
     * Connectors card rendered exactly that.
     *
     * The two numbers count different populations: `matched` is pages GOOGLE has
     * impressions for, `crawled` is pages OUR crawler reached. Google legitimately
     * knows more pages than a depth-limited crawl finds, so the denominator is
     * not a superset of the numerator and the fraction is only meaningful while
     * it holds. When it doesn't, state the number we are sure of and drop the
     * comparison rather than printing an impossible ratio on the one card whose
     * whole job is to say whether the data can be trusted.
     */
    const of = (n: number) =>
        crawled !== null && n <= crawled ? `${n} of ${crawled} pages matched` : `${n} pages matched`;

    // The catalog is the first gate: without a grant there is nothing to
    // reconcile, and "not connected" is a complete, honest answer.
    if (!connector || connector.status === "not_connected") {
        return {
            kind: "not_connected",
            tone: "idle",
            label: "Not connected",
            detail: "Connect Google Search Console to see which searches this site appears for, where it ranks, and which pages earn the clicks.",
            matchedPages: null,
            crawledPages: crawled,
            hasData: false,
        };
    }

    if (connector.status === "action_needed") {
        return {
            kind: "action_needed",
            tone: "warn",
            label: "Setup unfinished",
            detail: "The connection was started but never finished, so no Search Console data is being read yet.",
            matchedPages: null,
            crawledPages: crawled,
            hasData: false,
        };
    }

    // Only ever on an explicit true. An absent field means the BE has not
    // validated the property, which must not read as "the property is right".
    if (connector.propertyMismatch === true) {
        const property = connector.propertyUrl ? `The connected property (${connector.propertyUrl})` : "The connected property";
        const site = siteDomain ? ` Reconnect and pick the property for ${siteDomain}.` : "";
        return {
            kind: "wrong_property",
            tone: "warn",
            label: "Wrong property",
            detail: `${property} does not cover the site Oura crawls, so no row can ever match.${site}`,
            matchedPages: 0,
            crawledPages: crawled,
            hasData: false,
        };
    }

    // Connected, and the scan has no search block at all. A run finalized
    // before search performance existed is not a broken connection, and saying
    // so is different from saying nothing matched.
    if (!search) {
        return {
            kind: "not_measured",
            tone: "warn",
            label: pageClicks ? "Connected, page clicks only" : "Connected, not measured",
            detail: pageClicks
                ? "This scan carries page clicks only. The next scan adds searches, positions and totals."
                : "This scan ran before Oura read Search Console. The next scan is the first that can carry search numbers.",
            matchedPages: null,
            crawledPages: crawled,
            hasData: false,
        };
    }

    // The catalog and the scan disagree: a grant exists, and the run still
    // reports no Search Console pull. This is the discrepancy worth surfacing
    // loudest, because every screen downstream renders as if nothing were
    // connected while the connectors tab shows green.
    if (!search.connected) {
        return {
            kind: "no_pull",
            tone: "warn",
            label: "Connected, no data pulled",
            detail: "The account is linked, but the last scan pulled nothing from Search Console. That is either a property that does not cover this site, or a grant that arrived after the scan ran.",
            matchedPages: null,
            crawledPages: crawled,
            hasData: false,
        };
    }

    // Green requires the SAME thing the Pages table requires: per-page rows.
    // `hasSearchRows` deliberately counts keywords and totals too — that is the
    // right question for "can the Search screen show anything at all", but it
    // is the wrong bar for "Connected": a payload with keywords and no page
    // rollup used to earn a green tick beside a table of dashes.
    if ((search.pages?.length ?? 0) > 0) {
        return {
            kind: "matched",
            tone: "ok",
            label: matched !== null ? `Connected, ${of(matched)}` : "Connected",
            detail: "",
            matchedPages: matched,
            crawledPages: crawled,
            hasData: true,
        };
    }

    // Real data arrived — keywords, totals — but no per-page rollup, so the
    // per-page surfaces (the Pages table, per-page metrics) stay empty. Named
    // as its own state so the connector card and the Pages nudge both say what
    // is actually missing instead of contradicting each other.
    if (hasSearchRows(search)) {
        return {
            kind: "keywords_only",
            tone: "warn",
            label: "Connected, no page rows",
            detail: "Search Console returned search queries and totals, but no per-page rows — so per-page numbers cannot be shown yet. A refresh, or the next scan, is what fills them in.",
            matchedPages: 0,
            crawledPages: crawled,
            hasData: true,
        };
    }

    // Pulled, nothing back. `lastSync: null` is the one cause we can prove.
    if (!connector.lastSync) {
        return {
            kind: "never_synced",
            tone: "warn",
            label: "Connected, not synced yet",
            detail: "Search Console data lags 2 to 3 days, so a connection made today has nothing to hand back yet. The first scan after that window is the one to judge it on.",
            matchedPages: null,
            crawledPages: crawled,
            hasData: false,
        };
    }

    return {
        kind: "no_match",
        tone: "warn",
        label: `Connected, ${of(0)}`,
        detail: "Search Console returned no rows for any page Oura crawled. The two causes worth checking are a property that covers a different host or protocol, and a site too new to have collected impressions.",
        matchedPages: 0,
        crawledPages: crawled,
        hasData: false,
    };
}

/** The `gsc` entry, or null when the catalog has not loaded or omits it. */
export function findGscConnector(connectors: DataConnector[] | null | undefined): DataConnector | null {
    return connectors?.find((c) => c.key === "gsc") ?? null;
}
